import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import CollaboratorSelector, { Collaborator } from './CollaboratorSelector';
// Teams & departments as collaborators (G-6).
import TeamConnectorSection, { ConnectedTeamsList } from './TeamConnectorSection';
import DepartmentConnectorSection, { ConnectedDepartmentsList } from './DepartmentConnectorSection';
import Button from './TailwindComponents/Button';
import Badge from './TailwindComponents/Badge';
import Dropdown from './TailwindComponents/Dropdown';
import ProfileImage from '../Header/ProfileImage';
import { fetchConnectedTeams, fetchConnectedDepartments } from '../../store/actions/collaboratorActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { hideSidePanel } from '../../store/actions/sidePanelActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { formatDateTime } from '../../utils/functions';
import { PiTrashLight, PiUserLight, PiUsersThreeLight, PiStarLight, PiStarFill } from 'react-icons/pi';
import { normalise } from './access/accessModel';

interface ResourceCollaboratorsSidePanelProps {
  resourceUuid: string;
  resourceType: string;
  collaborators: any[];
  currentUserUuid?: string;
  onUpdate?: () => void;
  canAdmin?: boolean;
  /**
   * `collab` carries the row the panel already resolved (name, email,
   * initials, avatar, numeric user_id). It is optional so existing consumers
   * keep working, but a resource whose backend stores those fields should
   * take them from here rather than sending a bare uuid and letting the
   * record land nameless.
   */
  addCollaborator: (resourceUuid: string, userUuid: string, access: string, collab?: Collaborator) => Promise<any>;
  removeCollaborator: (resourceUuid: string, collaboratorUuid: string) => Promise<any>;
  updateAccess: (resourceUuid: string, collaboratorUuid: string, access: string) => Promise<any>;
  // Header customization
  title?: string;
  subMessage?: string;
  icon?: React.ReactNode;
  // Lead extension (clients use this; other resources use creator/admin role detection)
  supportsLead?: boolean;
  isLead?: (collab: any) => boolean;
  setLead?: (resourceUuid: string, collaboratorUuid: string) => Promise<any>;
  ownerLabel?: string; // e.g. "Lead" for clients, "Owner" otherwise
}

const ACCESS_OPTIONS = [
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit' },
];

// Includes 'admin' for backward-compatible read-only rendering of legacy rows.
const ACCESS_LABELS: Record<string, string> = {
  view: 'View',
  edit: 'Edit',
  admin: 'Admin',
};

const SOURCE_LABELS: Record<string, string> = {
  team: 'via team',
  department: 'via department',
  org: 'via org',
};

const ResourceCollaboratorsSidePanel: React.FC<ResourceCollaboratorsSidePanelProps> = ({
  resourceUuid,
  resourceType,
  collaborators: initialCollaborators,
  currentUserUuid,
  onUpdate,
  canAdmin = true,
  addCollaborator,
  removeCollaborator,
  updateAccess,
  // `title` / `subMessage` / `icon` are intentionally NOT rendered here any
  // more: the GlobalSidePanel chrome already shows the panel title + a single
  // consistent access icon. Rendering them again produced a duplicate
  // header - a lone floating resource icon on its own row under the real
  // header (UI feedback #3). Kept in the interface for caller compatibility.
  supportsLead = false,
  isLead,
  setLead,
  ownerLabel,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [pendingLeadUuid, setPendingLeadUuid] = useState<string | null>(null);
  // Teams & departments connected to this resource (G-6).
  const [connectedTeams, setConnectedTeams] = useState<
    Array<{ team_uuid: string; team_name: string; member_count: number }>
  >([]);
  const [connectedDepartments, setConnectedDepartments] = useState<
    Array<{ department_uuid: string; department_name: string; member_count: number }>
  >([]);
  const loadConnectedTeams = useCallback(async () => {
    const teams = await (dispatch as any)(fetchConnectedTeams(resourceType, resourceUuid));
    setConnectedTeams(teams || []);
  }, [dispatch, resourceType, resourceUuid]);
  const loadConnectedDepartments = useCallback(async () => {
    const depts = await (dispatch as any)(fetchConnectedDepartments(resourceType, resourceUuid));
    setConnectedDepartments(depts || []);
  }, [dispatch, resourceType, resourceUuid]);
  useEffect(() => {
    loadConnectedTeams();
    loadConnectedDepartments();
  }, [loadConnectedTeams, loadConnectedDepartments]);

  // Identify the ONE owner/creator (or lead, when supportsLead): the creator
  // row when there is one, otherwise the first admin.
  const ownerCollaborator = useMemo(
    () =>
      supportsLead && isLead
        ? initialCollaborators.find((c: any) => isLead(c))
        : initialCollaborators.find((c: any) => c.role === 'creator')
          || initialCollaborators.find((c: any) => c.access === 'admin'),
    [initialCollaborators, supportsLead, isLead]
  );

  const creatorUuid = useMemo(() => {
    return ownerCollaborator?.user_id ? String(ownerCollaborator.user_id) : currentUserUuid || '';
  }, [ownerCollaborator, currentUserUuid]);

  // Predicate: true only for THE owner/lead row. B6 (feedback round 3): this
  // used to match every admin, so with two or more admins (common: a table
  // deep-dive copies its admins into the review) one showed as "Owner" and
  // the rest - often the current user - vanished from the panel.
  const isOwnerRow = useMemo(
    () => (c: any) =>
      supportsLead && isLead
        ? isLead(c)
        : !!ownerCollaborator && c.source !== 'team'
          && String(c.user_id ?? c.collaborator_id ?? '') === String(ownerCollaborator.user_id ?? ownerCollaborator.collaborator_id ?? ''),
    [supportsLead, isLead, ownerCollaborator]
  );

  // Separate collaborators by source
  const { teamCollaborators, directCollaborators } = useMemo(() => {
    const nonOwner = initialCollaborators.filter((c: any) => !isOwnerRow(c));
    const teamCollabs: any[] = [];
    const directCollabs: any[] = [];
    for (const c of nonOwner) {
      if (c.source === 'team') {
        teamCollabs.push(c);
      } else {
        directCollabs.push(c);
      }
    }
    return { teamCollaborators: teamCollabs, directCollaborators: directCollabs };
  }, [initialCollaborators, isOwnerRow]);

  // All non-owner collaborators mapped to Collaborator shape
  const mappedInitial: Collaborator[] = useMemo(
    () =>
      initialCollaborators
        .filter((c: any) => !isOwnerRow(c))
        .map((c: any) => ({
          user_id: c.user_id,
          collaborator_uuid: c.collaborator_uuid || String(c.user_id),
          name: c.name || '',
          email: c.email || '',
          initials: c.initials || '',
          profile_picture: c.profile_picture || null,
          access: c.access || 'view',
          source: (c.source as Collaborator['source']) || 'direct',
          granted_via_uuid: c.granted_via_uuid || undefined,
          shared_by_user: c.shared_by_user || undefined,
          shared_at: c.shared_at || undefined,
        })),
    [initialCollaborators, isOwnerRow]
  );

  const [pendingCollaborators, setPendingCollaborators] = useState<Collaborator[]>(mappedInitial);

  /*
    EVERY CHANGE SAVES AS IT HAPPENS (feedback round 3: "two Add buttons").

    Teams and departments were added the moment you pressed Add; people were
    only STAGED by Add and written by a footer Save - two Add buttons that
    meant different things. People now behave like teams: add, remove, change
    access or set the lead and it is written immediately (optimistically,
    rolled back with a message if the server refuses). The footer is "Done".
  */
  const failed = () =>
    dispatch(
      showFeedbackModal({
        showModal: true,
        modalType: 'error',
        message: t('Failed to update access'),
        duration: 3000,
      })
    );

  const handleCollaboratorsChange = async (next: Collaborator[]) => {
    const known = new Set(pendingCollaborators.map((c) => c.user_id));
    const added = next.filter((c) => !known.has(c.user_id));
    if (added.length === 0) {
      setPendingCollaborators(next);
      return;
    }
    setIsSaving(true);
    setPendingCollaborators(next);
    const kept: Collaborator[] = [];
    for (const collab of added) {
      try {
        await addCollaborator(
          resourceUuid,
          (collab.collaborator_uuid || String(collab.user_id)) as string,
          collab.access || 'view',
          collab
        );
        kept.push(collab);
      } catch {
        failed();
      }
    }
    if (kept.length !== added.length) {
      const keptIds = new Set(kept.map((c) => c.user_id));
      setPendingCollaborators((prev) =>
        prev.filter((c) => known.has(c.user_id) || keptIds.has(c.user_id))
      );
    }
    setIsSaving(false);
    if (kept.length) onUpdate?.();
  };

  const handleAccessChange = async (collab: Collaborator, newAccess: Collaborator['access']) => {
    const before = collab.access;
    setPendingCollaborators((prev) =>
      prev.map((c) => (c.user_id === collab.user_id ? { ...c, access: newAccess } : c))
    );
    try {
      await updateAccess(resourceUuid, collab.collaborator_uuid || '', newAccess || 'view');
      onUpdate?.();
    } catch {
      setPendingCollaborators((prev) =>
        prev.map((c) => (c.user_id === collab.user_id ? { ...c, access: before } : c))
      );
      failed();
    }
  };

  const handleRemoveCollaborator = (collab: Collaborator) => {
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Remove access'),
        subMsg: t(
          'Remove {{name}} from this resource? Any access granted through this resource will be automatically revoked.',
          { name: collab.name || collab.email || 'this user' }
        ),
        isDest: true,
        onConfirm: async () => {
          dispatch(hideModal());
          try {
            await removeCollaborator(resourceUuid, collab.collaborator_uuid || '');
            setPendingCollaborators((prev) =>
              prev.filter((c) => c.user_id !== collab.user_id)
            );
            onUpdate?.();
          } catch {
            failed();
          }
        },
      })
    );
  };

  const handleSetLead = async (collaboratorUuid: string) => {
    if (!setLead || pendingLeadUuid === collaboratorUuid) return;
    const before = pendingLeadUuid;
    setPendingLeadUuid(collaboratorUuid);
    try {
      await setLead(resourceUuid, collaboratorUuid);
      onUpdate?.();
    } catch {
      setPendingLeadUuid(before);
      failed();
    }
  };

  // Group team collaborators by team name for display
  const teamGroups = useMemo(() => {
    const groups: Record<string, Collaborator[]> = {};
    for (const collab of pendingCollaborators) {
      if (collab.source === 'team' && collab.granted_via_uuid) {
        const teamName =
          connectedTeams.find((ct) => ct.team_uuid === collab.granted_via_uuid)?.team_name ||
          t('Team');
        if (!groups[teamName]) groups[teamName] = [];
        groups[teamName].push(collab);
      }
    }
    return groups;
  }, [pendingCollaborators, connectedTeams, t]);

  const individualCollaborators = useMemo(
    () => pendingCollaborators.filter((c) => c.source !== 'team' || !c.granted_via_uuid),
    [pendingCollaborators]
  );

  return (
    <div className="flex flex-col h-full">
      {/* Add new collaborators - admin only. PEOPLE gets the SAME section
          header pattern (icon + sentence-case label) as Teams and Departments
          below, so the three add-flows read as one consistent family
          (UI feedback #3). */}
      {canAdmin && (
        <div className="px-4 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <PiUserLight className="w-4 h-4 text-primary-600" />
            <p className="text-xs font-medium text-fg-muted">
              {t('People')}
            </p>
          </div>
          <CollaboratorSelector
            collaborators={pendingCollaborators}
            onCollaboratorsChange={handleCollaboratorsChange}
            allowedPermissions={['view', 'edit']}
            adminUuid={creatorUuid}
            showOwnerSection={false}
            hideSelectedList
          />
        </div>
      )}

      {/* Teams & departments as collaborators (G-6). */}
      <TeamConnectorSection
        resourceUuid={resourceUuid}
        resourceType={resourceType}
        connectedTeams={connectedTeams}
        onTeamConnected={() => {
          loadConnectedTeams();
          onUpdate?.();
        }}
        onTeamDisconnected={() => {
          loadConnectedTeams();
          onUpdate?.();
        }}
        canAdmin={canAdmin}
        existingCollaboratorUuids={[
          ...pendingCollaborators.map(c => c.collaborator_uuid || String(c.user_id || '')),
          ...(ownerCollaborator ? [ownerCollaborator.collaborator_uuid || String(ownerCollaborator.user_id || '')] : []),
          ...(currentUserUuid ? [currentUserUuid] : []),
        ].filter(Boolean)}
      />
      <DepartmentConnectorSection
        resourceUuid={resourceUuid}
        resourceType={resourceType}
        connectedDepartments={connectedDepartments}
        onDepartmentConnected={() => {
          loadConnectedDepartments();
          onUpdate?.();
        }}
        canAdmin={canAdmin}
      />

      {/* Unified collaborators list */}
      <div className="flex-1 overflow-y-auto custom-scrollbar-main px-5 py-3">
        {!ownerCollaborator && pendingCollaborators.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-gray-500">
            <PiUserLight className="w-8 h-8 mb-2 opacity-40" />
            <p className="text-sm">{t('No one has access yet')}</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {/* Owner row - prominently highlighted */}
            {ownerCollaborator && (
              <div className="flex items-center gap-3 py-3 px-3 rounded-lg bg-primary-50/50 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-900/20 mb-2">
                <ProfileImage
                  profilePicture={normalise(ownerCollaborator).avatar}
                  email={ownerCollaborator.email || ''}
                  initials={ownerCollaborator.initials || '??'}
                  name={ownerCollaborator.name || ''}
                  size="w-9 h-9"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {ownerCollaborator.name || ownerCollaborator.email}
                    </p>
                    <Badge color={supportsLead ? 'blue' : 'indigo'}>
                      {t(ownerLabel || (supportsLead ? 'Lead' : 'Owner'))}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                    {ownerCollaborator.email}
                  </p>
                </div>
                {supportsLead && (
                  <PiStarFill className="w-4 h-4 text-warning-500 flex-shrink-0" />
                )}
              </div>
            )}

            {/* Team-grouped collaborators */}
            {Object.entries(teamGroups).map(([teamName, members]) => (
              <div key={teamName} className="mt-3">
                <div className="flex items-center gap-2 px-2 mb-1">
                  <PiUsersThreeLight className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {teamName}
                  </p>
                </div>
                {members.map((collab) => (
                  <CollaboratorRow
                    key={collab.user_id}
                    collab={collab}
                    initialCollaborators={initialCollaborators}
                    mappedInitial={mappedInitial}
                    canAdmin={canAdmin}
                    onAccessChange={handleAccessChange}
                    onRemove={handleRemoveCollaborator}
                    supportsLead={supportsLead}
                    isPendingLead={
                      !!pendingLeadUuid &&
                      pendingLeadUuid === String(collab.collaborator_uuid || collab.user_id)
                    }
                    onSetLead={handleSetLead}
                    t={t}
                  />
                ))}
              </div>
            ))}

            {/* Individual (direct) collaborators */}
            {individualCollaborators.length > 0 && Object.keys(teamGroups).length > 0 && (
              <div className="mt-3 px-2 mb-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {t('Individual access')}
                </p>
              </div>
            )}
            {individualCollaborators.map((collab) => (
              <CollaboratorRow
                key={collab.user_id}
                collab={collab}
                initialCollaborators={initialCollaborators}
                mappedInitial={mappedInitial}
                canAdmin={canAdmin}
                onAccessChange={handleAccessChange}
                onRemove={handleRemoveCollaborator}
                t={t}
              />
            ))}
          </div>
        )}
      </div>

      {/* Connected teams & departments (G-6). */}
      <ConnectedTeamsList
        resourceUuid={resourceUuid}
        resourceType={resourceType}
        connectedTeams={connectedTeams}
        onTeamDisconnected={() => { loadConnectedTeams(); onUpdate?.(); }}
        canAdmin={canAdmin}
      />
      <ConnectedDepartmentsList
        resourceUuid={resourceUuid}
        resourceType={resourceType}
        connectedDepartments={connectedDepartments}
        onDepartmentDisconnected={() => { loadConnectedDepartments(); onUpdate?.(); }}
        canAdmin={canAdmin}
      />

      {/* Footer */}
      {/* Changes are already saved - one way out. */}
      <div className="border-t border-hairline-soft px-5 py-4 flex justify-end gap-2">
        <Button variant="primary" size="small" onClick={() => dispatch(hideSidePanel())} disabled={isSaving}>
          {isSaving ? t('Saving...') : t('Done')}
        </Button>
      </div>
    </div>
  );
};

/* ─── Extracted row component for reuse ─── */

interface CollaboratorRowProps {
  collab: Collaborator;
  initialCollaborators: any[];
  mappedInitial: Collaborator[];
  canAdmin: boolean;
  onAccessChange: (collab: Collaborator, access: Collaborator['access']) => void;
  onRemove: (collab: Collaborator) => void;
  supportsLead?: boolean;
  isPendingLead?: boolean;
  onSetLead?: (collaboratorUuid: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

const CollaboratorRow: React.FC<CollaboratorRowProps> = ({
  collab,
  initialCollaborators,
  mappedInitial,
  canAdmin,
  onAccessChange,
  onRemove,
  supportsLead = false,
  isPendingLead = false,
  onSetLead,
  t,
}) => {
  const original = initialCollaborators.find((c: any) => c.user_id === collab.user_id);
  const sharedByUser = original?.shared_by_user;
  const sharedAt = original?.shared_at;
  const source = original?.source || collab.source || 'direct';
  const isNew = !mappedInitial.some((m) => m.user_id === collab.user_id);
  // Legacy non-owner rows with access === 'admin' render read-only - Admin is
  // no longer assignable but existing data must not silently downgrade.
  const isLegacyAdmin = collab.access === 'admin';

  const addedByName = sharedByUser?.full_name || sharedByUser?.name;

  return (
    /*
      A TWO-LINE RECORD, read across in columns.

      It used to be `items-start` over a three-line text stack (name+badges /
      email / "Added by X · date"), so the 32px avatar and the compact access
      dropdown both pinned to the top of a tall ragged row and the provenance
      ran into the email. Now: identity left, provenance in its own quiet
      right-aligned column, control on the right - all vertically centred on
      one another, so a list of collaborators scans down its columns.
    */
    <div className="flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
      <div className="flex-shrink-0 flex items-center justify-center w-9">
        <ProfileImage
          profilePicture={normalise(collab).avatar}
          email={collab.email || ''}
          initials={collab.initials || '??'}
          name={collab.name || ''}
          size="w-8 h-8"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {collab.name || collab.email}
          </p>
          {source !== 'direct' && SOURCE_LABELS[source] && (
            <Badge color="gray">{t(SOURCE_LABELS[source])}</Badge>
          )}
          {isNew && <Badge color="green">{t('New')}</Badge>}
          {isPendingLead && <Badge color="blue">{t('Lead (pending)')}</Badge>}
        </div>
        <p
          className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1"
          title={collab.email}
        >
          {collab.email}
        </p>
      </div>

      {/* Provenance - its own column, so it never crowds the email. Hidden on
          a narrow panel, where the row is already at its useful minimum. */}
      {(addedByName || sharedAt) && (
        <div className="hidden sm:block flex-shrink-0 text-end leading-tight max-w-[150px]">
          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
            {addedByName ? t('Added by {{name}}', { name: addedByName }) : t('Added')}
          </p>
          {sharedAt && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
              {formatDateTime(sharedAt)}
            </p>
          )}
        </div>
      )}

      {/* Access level */}
      <div className="flex-shrink-0 flex items-center gap-1">
        {canAdmin && !isLegacyAdmin ? (
          <>
            {/* Not `compact`: the tight variant sat at a different height
                from the avatar and left the value crowding its caret. Full
                density, a stated width, and a popup at least as wide as the
                control so an option can never be clipped. */}
            <Dropdown
              options={ACCESS_OPTIONS}
              value={collab.access || 'view'}
              onChange={(val) =>
                onAccessChange(
                  collab,
                  (Array.isArray(val) ? val[0] : val) as Collaborator['access']
                )
              }
              popupMinWidth={200}
            />
            {supportsLead && onSetLead && !isNew && (
              <button
                onClick={() =>
                  onSetLead(String(collab.collaborator_uuid || collab.user_id))
                }
                className="p-1 rounded text-gray-400 hover:text-warning-500 hover:bg-warning-50 dark:hover:bg-warning-900/20 transition-colors"
                title={t('Set as lead')}
              >
                {isPendingLead ? (
                  <PiStarFill className="w-4 h-4 text-warning-500" />
                ) : (
                  <PiStarLight className="w-4 h-4" />
                )}
              </button>
            )}
            <button
              onClick={() => onRemove(collab)}
              className="p-1 rounded text-gray-400 hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
              title={t('Remove')}
            >
              <PiTrashLight className="w-4 h-4" />
            </button>
          </>
        ) : (
          <Badge
            color={
              collab.access === 'edit'
                ? 'blue'
                : collab.access === 'admin'
                ? 'slate'
                : 'gray'
            }
          >
            {t(ACCESS_LABELS[collab.access] || collab.access)}
          </Badge>
        )}
      </div>
    </div>
  );
};

export default ResourceCollaboratorsSidePanel;
